const { Cycler } = window.BAO

class CollectionSwitch extends window.BAO.CustomElement() {
  CURRENT_STYLE_ATTRIBUTE = 'current-style'
  ALLOWED_STYLES = ['default', 'lifestyle']
  NAVIGATION_KEYS = ['ArrowLeft', 'ArrowRight', 'Home', 'End']
  CLASSES = {
    default: 'clc-ProductGrid-default',
    lifestyle: 'clc-ProductGrid-lifestyle',
  }

  connectedCallback () {
    if (!this.els.trigger?.elements?.length) {
      throw new Error(
        `<${this.localName}> needs an element with data-collection-switch-el=trigger to work"`,
      )
    }

    this.handleTriggerClick = this.handleTriggerClick.bind(this)
    this.handleTriggerKeydown = this.handleTriggerKeydown.bind(this)
    this.cycler = new Cycler(this.ALLOWED_STYLES)
    this.cycler.set(
      this.cycler.items.findIndex((i) => i === this.currentStyle),
    )

    this.triggers.forEach((triggerEl) => {
      triggerEl.addEventListener('click', this.handleTriggerClick, {
        passive: true,
      })

      triggerEl.addEventListener('keydown', this.handleTriggerKeydown)
    })

    this.syncTriggerAccessibility({ initialState: true })
    this.ensureLiveRegion()
  }

  disconnectedCallback () {
    this.triggers.forEach((triggerEl) => {
      triggerEl.removeEventListener('click', this.handleTriggerClick)
      triggerEl.removeEventListener('keydown', this.handleTriggerKeydown)
    })
  }

  async handleTriggerClick (event) {
    const triggerStyle = event.currentTarget.dataset.style
    let currentStyle = triggerStyle || this.cycler.next()

    // Preserve toggle behavior: clicking the active control flips to the opposite style.
    if (triggerStyle === this.currentStyle) {
      currentStyle =
        this.ALLOWED_STYLES[
          (this.currentStyleIndex + 1) % this.ALLOWED_STYLES.length
        ]
    }

    this.cycler.set(
      this.cycler.items.findIndex((item) => item === currentStyle),
    )

    this.applyStyle(currentStyle)
  }

  handleTriggerKeydown (event) {
    if (!this.NAVIGATION_KEYS.includes(event.key)) {
      return
    }

    event.preventDefault()

    let nextIndex = this.currentStyleIndex

    if (event.key === 'ArrowRight') {
      nextIndex = (this.currentStyleIndex + 1) % this.ALLOWED_STYLES.length
    }

    if (event.key === 'ArrowLeft') {
      nextIndex =
        (this.currentStyleIndex - 1 + this.ALLOWED_STYLES.length) %
        this.ALLOWED_STYLES.length
    }

    if (event.key === 'Home') {
      nextIndex = 0
    }

    if (event.key === 'End') {
      nextIndex = this.ALLOWED_STYLES.length - 1
    }

    const nextStyle = this.ALLOWED_STYLES[nextIndex]
    const nextTrigger = this.triggers.find(
      (triggerEl) => triggerEl.dataset.style === nextStyle,
    )

    nextTrigger?.focus()
    this.cycler.set(nextIndex)
    this.applyStyle(nextStyle)
  }

  applyStyle (currentStyle) {
    if (!this.ALLOWED_STYLES.includes(currentStyle)) {
      return
    }

    this.setCartAttribute(currentStyle)
    this.currentStyle = currentStyle
    this.syncThemeSwitchers(currentStyle)
    this.syncTriggerAccessibility()
    this.announceStyle(currentStyle)

    window.BAO.eventBus.dispatchEvent(window.BAO.EVENTS.THEME_SWITCH, {
      currentStyle,
    })
  }

  syncThemeSwitchers (currentStyle) {
    this.allOtherThemeSwitches.forEach((el) => {
      el.currentStyle = currentStyle
      el.cycler.set(el.cycler.items.findIndex((item) => item === currentStyle))
      el.syncTriggerAccessibility()
    })
  }

  syncTriggerAccessibility ({ initialState = false } = {}) {
    this.triggers.forEach((triggerEl) => {
      const isActive = triggerEl.dataset.style === this.currentStyle

      triggerEl.setAttribute('aria-selected', String(isActive))
      triggerEl.setAttribute(
        'aria-pressed',
        String(initialState ? false : isActive),
      )
      triggerEl.setAttribute('tabindex', isActive ? '0' : '-1')
    })
  }

  ensureLiveRegion () {
    if (this.liveRegion) {
      return
    }

    const existingLiveRegion = this.querySelector(
      '[data-collection-switch-el="live-region"]',
    )

    if (existingLiveRegion) {
      this.liveRegion = existingLiveRegion
      return
    }

    const liveRegion = document.createElement('span')
    liveRegion.className = 'util-ScreenReaderOnly'
    liveRegion.setAttribute('role', 'status')
    liveRegion.setAttribute('aria-live', 'polite')
    liveRegion.setAttribute('aria-atomic', 'true')
    liveRegion.setAttribute('data-collection-switch-el', 'live-region')

    this.appendChild(liveRegion)
    this.liveRegion = liveRegion
  }

  announceStyle (currentStyle) {
    if (!this.liveRegion) {
      return
    }

    const styleLabel =
      currentStyle === 'lifestyle' ? 'lifestyle view' : 'default view'
    const message = `Showing ${styleLabel}`

    if (this.liveRegion.textContent === message) {
      this.liveRegion.textContent = ''
    }

    this.liveRegion.textContent = message
  }

  setCartAttribute (style) {
    // eslint-disable-next-line compat/compat
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        attributes: {
          current_style: style,
        },
      }),
    })
  }

  get currentStyle () {
    return (
      this.getAttribute(this.CURRENT_STYLE_ATTRIBUTE) || this.ALLOWED_STYLES[0]
    )
  }

  set currentStyle (value) {
    this.setAttribute(this.CURRENT_STYLE_ATTRIBUTE, value)

    this.externalEls.body.element.classList.toggle(
      this.CLASSES.default,
      value === 'default',
    )
    this.externalEls.body.element.classList.toggle(
      this.CLASSES.lifestyle,
      value === 'lifestyle',
    )
  }

  get allOtherThemeSwitches () {
    // eslint-disable-next-line compat/compat
    return Array.from(document.querySelectorAll(this.localName)).filter(
      (el) => el !== this,
    )
  }

  get triggers () {
    return this.els.trigger?.elements || []
  }

  get currentStyleIndex () {
    const index = this.ALLOWED_STYLES.findIndex(
      (item) => item === this.currentStyle,
    )
    return index === -1 ? 0 : index
  }
}

if (!customElements.get('collection-switch')) {
  customElements.define('collection-switch', CollectionSwitch)
}
